// File: frontend/src/pages/Plato/components/KnowledgeContainer/KnowledgeContainer.js

import React, { useRef, useEffect } from 'react';
import styles from './KnowledgeContainer.module.css';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { BookOpen } from 'lucide-react';

const KnowledgeContainer = ({ entries, title, summary, isLoading }) => {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const lastEntryRef = useRef(null);

  // Scroll to bottom when new entries are added
  useEffect(() => {
    if (lastEntryRef.current) {
      lastEntryRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries]);

  if (!entries || entries.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <div className={styles.emptyStateIcon}>
          <BookOpen size={48} />
        </div>
        <h2>{t('plato.empty_state.title', 'Welcome to Plato')}</h2>
        <p>{t('plato.empty_state.description', 'Plato is a sophisticated platform for transferring, refining, and amplifying knowledge across various domains and disciplines. Start by entering your text below.')}</p>
      </div>
    );
  }

  return (
    <div className={styles.knowledgeContainer} ref={containerRef}>
      {title && (
        <div className={styles.knowledgeHeader}>
          <h1 className={styles.knowledgeTitle}>{title}</h1>
          {summary && <p className={styles.knowledgeSummary}>{summary}</p>}
        </div>
      )}
      
      <div className={styles.entriesContainer}>
        {entries.map((entry, index) => {
          const isLastEntry = index === entries.length - 1;
          const ref = isLastEntry ? lastEntryRef : null;
          
          return (
            <div 
              key={`${entry.role}-${index}`}
              className={`${styles.entryWrapper} ${styles[entry.role]}`}
              ref={ref}
            >
              <div className={styles.entryContent}>
                {entry.isTyping ? (
                  <div className={styles.typingIndicator}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  entry.content.map((contentItem, contentIndex) => {
                    if (contentItem.type === 'text') {
                      return (
                        <div 
                          key={`text-${contentIndex}`} 
                          className={styles.textContent}
                        >
                          {contentItem.text}
                        </div>
                      );
                    } else if (contentItem.type === 'image') {
                      return (
                        <img 
                          key={`image-${contentIndex}`}
                          src={contentItem.url} 
                          alt={contentItem.alt || 'Knowledge image'} 
                          className={styles.imageContent}
                        />
                      );
                    } else if (contentItem.type === 'code') {
                      return (
                        <pre 
                          key={`code-${contentIndex}`}
                          className={styles.codeContent}
                        >
                          <code>{contentItem.code}</code>
                        </pre>
                      );
                    }
                    return null;
                  })
                )}
              </div>
              
              {entry.usage && (
                <div className={styles.usageInfo}>
                  <span>Tokens: {entry.usage.total_tokens}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KnowledgeContainer;